import { useStreamSelector } from "@launch10/use-langgraph-stream";
import type { StreamSnapshot } from "@launch10/use-langgraph-stream";
import { todosMerge, filesMerge, type Todo, type WebsiteGraphState } from "@shared";
import {
  useProjectStreamOptions,
  type StreamReducerMap,
} from "@hooks/useProjectStreamOptions";
import { useCallback, useEffect, useEffectEvent, useRef } from "react";
import { usePage, router } from "@inertiajs/react";
import { useQueryClient } from "@tanstack/react-query";
import { websiteKeys, useWebsitePages } from "@api/websites.hooks";
import { useSetPreviewRoute } from "@stores/previewStore";
import { useWebsiteKickoffStore } from "@stores/websiteKickoffStore";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface WebsitePageProps {
  /** `started` = the site has its own files (diverged from its forked template). */
  website?: { id?: number; started?: boolean };
  project?: { id?: number; uuid?: string };
  thread_id?: string;
  /** True when the thread id is minted but LangGraph has never seen it (no threads row). */
  thread_reserved?: boolean;
  [key: string]: unknown;
}

export type WebsiteSnapshot = StreamSnapshot<WebsiteGraphState>;

// ---------------------------------------------------------------------------
// Stream Options (used by Website.tsx → Chat.Root)
// ---------------------------------------------------------------------------

/**
 * Client-side mirror of the WebsiteAnnotation channel reducers, applied during
 * the streaming window (the server reducers only settle at the parent merge).
 *
 * - todos: merge by id, never downgrade status. Parallel subagents each stream
 *   a partial, snapshot-relative list (only their own todo flipped); a wholesale
 *   replace would let a later subagent's stale view re-open a completed todo.
 * - files: shallow merge by path, so a subagent's partial file map doesn't blow
 *   away files written by its siblings.
 *
 * Folded through both onUpdateEvent and the `__state_patch__` custom event by
 * useProjectStreamOptions. Everything else (agentIntents, creditStatus, …) is
 * covered by the base reducer map's `replace` default.
 */
const WEBSITE_STREAMING_REDUCERS: StreamReducerMap = {
  todos: (prev, next) => todosMerge(next as Todo[], prev as Todo[] | undefined),
  files: (prev, next) =>
    filesMerge(
      next as WebsiteGraphState["files"],
      prev as WebsiteGraphState["files"] | undefined
    ),
};

export function useWebsiteStreamOptions() {
  const page = usePage<WebsitePageProps>();

  const onThreadId = useCallback(
    (threadId: string) => {
      if (!threadId || threadId === "undefined" || threadId === "null") {
        console.error(
          "[useWebsiteChat] onThreadId called with invalid threadId:",
          threadId
        );
        return;
      }

      router.push({
        url: window.location.pathname,
        preserveState: true,
        component: "Website",
        props: {
          ...page.props,
          thread_id: threadId,
          // The thread just materialized in LangGraph, so it is no longer
          // reserved. Clear the flag here — `thread_reserved` is a one-shot
          // signal and this spread would otherwise preserve the stale `true`.
          // A later remount reads props on mount (useProjectStreamOptions'
          // `mountThreadReserved`); a stale `true` re-arms the deferred create
          // against an existing thread → 409.
          thread_reserved: false,
        },
      });
    },
    [page.props]
  );

  return useProjectStreamOptions<WebsiteGraphState>({
    assistantId: "website",
    onThreadId,
    fetchStateHistory: false,
    streamingReducers: WEBSITE_STREAMING_REDUCERS,
  });
}

// ---------------------------------------------------------------------------
// Typed Selector
// ---------------------------------------------------------------------------

export function useWebsiteSelector<T>(
  selector: (snapshot: WebsiteSnapshot) => T
): T {
  return useStreamSelector<T, WebsiteGraphState>(selector);
}

// ---------------------------------------------------------------------------
// Convenience Hooks
// ---------------------------------------------------------------------------

export function useWebsiteChatState<K extends keyof WebsiteGraphState>(key: K) {
  return useWebsiteSelector((s) => s.values?.[key]);
}

/** True while the agent is compacting conversation history (see CompactingIndicator). */
export function useWebsiteChatIsCompacting() {
  return useWebsiteSelector((s) => s.values?.isCompacting ?? false);
}

export function useWebsiteChatFullState() {
  return useWebsiteSelector((s) => s.values);
}

export function useWebsiteChatMessages() {
  return useWebsiteSelector((s) => s.messages);
}

export function useWebsiteChatIsLoading() {
  return useWebsiteSelector((s) => s.isStreaming);
}

export function useWebsiteChatIsLoadingHistory() {
  return useWebsiteSelector((s) => s.isLoadingHistory);
}

export function useWebsiteChatIsStreaming() {
  return useWebsiteSelector((s) => s.isStreaming);
}

export function useWebsiteChatSubmit() {
  return useWebsiteSelector((s) => s.submit);
}

export function useWebsiteChatStop() {
  return useWebsiteSelector((s) => s.stop);
}

/**
 * Returns whether the website is in its initial loading phase:
 * history is loading, or streaming with todos still in progress.
 * Stays on the todo list until ALL todos are completed, even if files have arrived.
 *
 * If the user has sent messages BUT no files exist yet (initial build failed),
 * we stay in loading mode so the loader shows during retries.
 * Once files exist, user messages switch us to chat mode permanently.
 */
export function useWebsiteChatIsInitialLoading() {
  // Latch "this site has ever had files". `s.values.files` flickers empty
  // mid-run — `files` is not a persisted graph channel; it arrives via late
  // node updates (afterAgent/websiteBuilder), so at the start of each run it
  // momentarily reads empty. Without the latch, an edit that writes todos sees
  // `hasFiles=false` during streaming, falls through to the todos branch, and
  // returns true → BuildStep swaps the live <WebsitePreview/> for the loader,
  // unmounting it. Remounting the preview mid-run drops the live view and can
  // leave it stale. The latch survives the flicker so an established site stays
  // in edit/chat mode through todos.
  // Durable seed: an established site (its files diverged from the forked
  // template) is `started` on page load — sourced from the DB, no graph run
  // needed. This is what keeps a built site in edit mode across reloads.
  // `useWebsitePagesSync` reloads this prop after each turn, so a same-session
  // first build flips the latch without a manual refresh.
  const everHadFiles = useRef(false);
  if (usePage<WebsitePageProps>().props.website?.started) everHadFiles.current = true;
  return useWebsiteSelector((s) => {
    const hasUserMessages = s.messages.some(
      (m: any) => m.type === "human" || m.role === "user"
    );
    // Established site (built before + the user has talked to it) → never fall
    // back to the full-screen loader, even while todos are open mid-edit.
    if (hasUserMessages && everHadFiles.current) return false;

    const todos = s.values?.todos as any[] | undefined;
    const hasTodos = todos && todos.length > 0;
    const allTodosCompleted =
      hasTodos && todos.every((t: any) => t.status === "completed");
    return s.isLoadingHistory || (s.isStreaming && hasTodos && !allTodosCompleted);
  });
}

// ---------------------------------------------------------------------------
// Side Effects
// ---------------------------------------------------------------------------

/**
 * Invalidates the pages query once per agent turn so the page picker picks
 * up new / renamed / deleted pages after a file write cascade. Pages are
 * derived from `src/pages/**.astro` files via the Rails `Page` model, so
 * the DB view only lands after the post-turn flush.
 */
export function useWebsitePagesSync() {
  const isStreaming = useWebsiteSelector((s) => s.isStreaming);
  const websiteId = useWebsiteSelector((s) => s.values?.websiteId);
  const queryClient = useQueryClient();
  const prevStreamingRef = useRef(isStreaming);

  useEffect(() => {
    const wasStreaming = prevStreamingRef.current;
    prevStreamingRef.current = isStreaming;

    if (wasStreaming && !isStreaming && websiteId) {
      queryClient.invalidateQueries({ queryKey: websiteKeys.pages(websiteId) });
      // Refresh the `started` prop after a turn so a same-session first build
      // flips the "established site" latch (the site now has its own files).
      // Also refresh `thread_reserved`: the first build materializes the LG
      // thread, so the server now computes `false`. Pulling the corrected value
      // means a later remount reads a fresh prop instead of the stale `true`
      // that re-arms the deferred create → 409.
      router.reload({ only: ["website", "thread_reserved"] });
    }
  }, [isStreaming, websiteId, queryClient]);
}

/**
 * Auto-switches the preview to a newly created page once the pages query
 * settles after `useWebsitePagesSync` invalidates it. The agent only adds
 * pages in response to a user request, so when one appears mid-session we
 * jump to it — no explicit langgraph signal needed.
 *
 * Only fires for a *single* page added against a non-empty baseline. Initial
 * generation arrives as the whole multi-page set at once (against an empty
 * baseline), and the pages query also returns `[]` before the first turn
 * completes — both cases must fall through to PagePicker's `/` seed rather
 * than jumping to whichever route happens to be ordered first.
 */
export function useAutoSelectNewPage() {
  const websiteId = useWebsiteSelector((s) => s.values?.websiteId);
  const { data: pages } = useWebsitePages(websiteId);
  const setActiveRoute = useSetPreviewRoute();
  const prevRoutesRef = useRef<Set<string> | null>(null);

  useEffect(() => {
    if (!pages) return;

    const currentRoutes = new Set(
      pages.map((p) => p.route).filter((r): r is string => !!r)
    );

    const prevRoutes = prevRoutesRef.current;
    prevRoutesRef.current = currentRoutes;

    // No baseline yet, or the baseline was empty (the pages query resolves to
    // `[]` before the first turn lands) — this is the initial populate, not a
    // mid-session add. Let PagePicker seed `/`.
    if (prevRoutes === null || prevRoutes.size === 0) return;

    const newRoutes = [...currentRoutes].filter((r) => !prevRoutes.has(r));

    // Jump only when exactly one page was added — a burst of new routes means
    // a regeneration, not a deliberate single-page add.
    if (newRoutes.length === 1) setActiveRoute(newRoutes[0]);
  }, [pages, setActiveRoute]);
}

/**
 * Auto-initialize the website generation when the page loads.
 * Sends websiteId/projectId as input to the graph via submit().
 *
 * Thread-addressable URLs mint the website chat's thread_id at creation, so
 * every visit arrives WITH a thread_id — its presence no longer means the
 * build has started. `thread_reserved` is the real signal: true = the id is
 * minted but LangGraph has never seen it, so the kickoff must still fire.
 */
export function useWebsiteAutoInit() {
  const { website, project, thread_id, thread_reserved } =
    usePage<WebsitePageProps>().props;
  const submit = useWebsiteSelector((s) => s.submit);
  const isLoading = useWebsiteSelector((s) => s.isStreaming);
  const chatId = useWebsiteSelector((s) => s.values?.chatId);
  // The arrival snapshot: was this already an existing/returning run, or a fresh
  // site whose kickoff is about to fire? Captured once (chatId flips truthy
  // during the run, so a reactive read would corrupt the snapshot).
  const wasExistingRun = useRef(!!chatId || (!!thread_id && !thread_reserved));
  const hasInitialized = useRef(wasExistingRun.current);

  // Publish the arrival snapshot so useServerPreviewDriver gates its first-mount
  // preview on the same fact — a fresh site must not build before its kickoff
  // run forks the template. See websiteKickoffStore.
  const publishExistingRun = useWebsiteKickoffStore((s) => s.publishExistingRun);
  useEffect(() => {
    publishExistingRun(wasExistingRun.current);
  }, [publishExistingRun]);

  const maybeInit = useEffectEvent(() => {
    if (hasInitialized.current) return;
    if (isLoading) return;
    if (!website?.id || !project?.id) return;

    hasInitialized.current = true;
    submit({ websiteId: website.id, projectId: project.id });
  });

  useEffect(() => {
    maybeInit();
  }, [website?.id, project?.id]);
}
